<!--
 * @Author: 
 * @Date: 2023-12-14 13:32:45
 * @LastEditors: Merlin
 * @LastEditTime: 2025-08-15 14:02:56
 * @Description: 工程根组件
-->
<template>
    <!-- ↓ input 置于顶部，便于先加载供调用 -->
    <input type="hidden"
        :id="sTokenKey"
        :value="sTK"
    />
    <!-- ↓ 移动到上方，以实现先加载，先遮罩 -->
    <Loading keyForControl="showLoading"/>
    <q-layout
        view="hHh lpr fFf"
        class="app text-white relative"
        v-if="isShowRoot"
    >
        <!-- ↓ 顶部 header -->
        <Header />
        <!-- ↓ 核心区 -->
        <main class="main-box min-h-screen overflow-x-hidden"
            :class="{'in-casino': oRoute.path.includes('/casino/')}"
        >
            <!-- 👇 版心，重要区域 -->
            <slot :key="reloadKey" />
            <!-- 👆 版心，重要区域 -->
            <Loading keyForControl="gameLoading" />
        </main>
    </q-layout>
    <!--
        ↑ 程序根节点
        ↓ 其它全局性组件
    -->
    <Providers />
    <ModalMessage v-if="globalStore?.pageLoaded" />
    <!-- ↓ 提示用户是否切换界面语言（仅在界面语言与后端记录不符时弹出） -->
    <ModalSwitchLang
        v-if="globalStore?.pageLoaded && bLangSwitcherVisible" 
        :lang="userStore.lang || ''"
    />
</template>

<script lang="ts" setup>
import useGlobalStore from '@/stores/global';
import useSettingsStore from '@/stores/settings';
import { useAuthStore, getServerSideTK, destroyToken } from '~/stores/auth';
import { useLoadingStore } from '@/stores/loading';
import { useUserStore } from '@/stores/user';
import { useGameIdListStore } from '@/stores/cansino/gameIdList';
import { useRatesStore } from '~/stores/cansino/rates';
// ----- ↑ store --------------
import { useAuthApi } from '~/api/auth';
// ----- ↑ socket, api --------------
import { inferNewLang, toRecordNewLanguage } from '@/i18n/langSettingFn';
import { getGameName } from '@/common/js/gameAbout';
import { enterGame } from '@/common/js/gameJoining';
import Cookies from 'js-cookie';

const authStore = useAuthStore();
const globalStore = useGlobalStore();
const userStore = useUserStore();
const settingStore = useSettingsStore();
// --- ↑ store 集中区 ------------------------
const oRoute = useRoute();
const sAuCode = oRoute.query.au;
const isShowRoot = ref(!sAuCode); // 没有 au 时可直接显示根节点。否则千万不要显示，由浏览器伺机显示  
const bLangSwitcherVisible = ref(false);
let oUserInfoPromise = null; // 结果为用户信息的 promise 对象
const sTK = useCookie(sTokenKey, { // 后端推给浏览器的 token 值，用于第3方登录 
    httpOnly: false, // 默认值是即 false 
    maxAge: 60, // 仅 60 秒有效，不宜久留 
    sameSite: 'lax', // 火狐等浏览器需要显式声明，否则可能拒绝写入/读取
});
const reloadKey = computed(() => oRoute.path.includes('redpacket') ? globalStore.redpacketReload : globalStore.markForReload);

// ============================================================== 
// ===== 🚩🚩 ↓ 这里是程序入口 🚩🚩 ==============================  
// ============================================================== 
if (import.meta.server) {
    if (btoa){
        const obj01 = {
            auth: socket.auth,
            id: socket.id,
            ids: socket['ids'],
            connected: socket.connected,
            uri: socket.io['uri'],
        };
        const str = btoa(JSON.stringify(obj01));
        const ts = Date.now().toString(32).slice(-2);
        Object.assign(globalStore, {
            specialValue: `${str.slice(0, 2)}${ts}${str.slice(2)}`,
        });
    }
    console.log('🥇 服务端执行 --------------------------------');
    // ↓ 在服务端提前查询数据用于推到客户端，
    const oPromiseAllSettled = Promise.allSettled([
        useGameIdListStore().initData(900),
        useRatesStore().initData(900),
    ]);
    // console.log('显示根节点？', isShowRoot.value); 
    if (sAuCode){
        const t01 = Date.now();
        const sToken = await getTokenStringByAU();
        console.log(
            `\nAuCode -> Token 转换(查询)耗时 ${Date.now() - t01}ms，详情如下`,
            `\nAuCode 👉 ${sAuCode}`,
            `\nToken 👉 ${(sToken || '✘').slice(0, 30)}...`,
        );
        // ↓ 查不到 token 说明出错了（少有发生）
        if (!sToken){
            console.log('💥 使用 au 查询 token 未成功');
            // 实测得出不应在此跳转，应保持用户的地址栏保持有 au 值，以便彰显当前正在"尝试登录" 
        }
        // ↓ 仅在收到 au 时服务器才覆盖浏览器 cookie 值 
        sTK.value = sToken || ''; 
    }
    await oPromiseAllSettled;
} else if (import.meta.client) {
    console.log('🎈 客户端执行2 --------------------------------');
    // ↓ 收到 au code 表示第三方登入进行中
    if (sAuCode) {
        if (!sTK.value){
            // 本站点做为 iframe 位于其它域名下时将执行至此 
            sTK.value = getServerSideTK();
        }
        if (sTK.value){
            console.log('[AU_LOGIN] Token 已保存到 authStore');
            authStore.setToken(sTK.value);
        }else{
            console.error('[AU_LOGIN] 登录未成功，无 tk 值'); 
            destroyToken(); 
            socket.disconnect();
        }
        sTK.value = ''; // 通过 setToken() 保存妥当后可以销毁 cookie 
    }
    // ↓ 使用判断来确保：仅在有资格查时才查（有 token 时才查） 
    if (authStore.trueToken){ 
        // ↓ 用于调试，以及测试人员的方便取值，需要保留  
        if (localStorage.getItem('showtoken') == '1' || import.meta.dev){ 
            console.log(`\nshowtoken ↓\n${authStore.trueToken}`); 
        }
        oUserInfoPromise = asyncAuthFetch();
    }
    // ↓ 监听浏览器缩放，用于控制页面变化等
    window.addEventListener('resize', globalStore.setInnerWidth);
    // ↓ 监听 ws 重连事件 
    socket['oTokenPromise_'].oPromise.then(fnReconnectHandler);
}

// ↓ 查询用户的信息，如余额，界面语言，
async function asyncAuthFetch (){
    console.log('asyncAuthFetch() 开始查询用户信息');
    // ↓ 目前发现苹果手机建立 ws 链接慢，导致这里设定的5秒超时会触发，现在改为为 9 秒 
    const iTimeOut = 9 * 1_000; 
    const sTimeout = 'timeout';
    const token = authStore.trueToken;
    // ↓ 如果没 toke 不再执行查询，因为查也查不到（通常不会到这里，也不应到这里，无 token 时不应调用此方法）
    if (!token) {
        throw new Error("Get user info failed: No token");
    }
    // ▼ 查询用户信息
    const data:any = await Promise.race([
        getUserInfo(),
        Sleep(iTimeOut).then(()=>{
            return {error: sTimeout};
        }),
    ]);
    // ↓ 打印适宜保留，它会被 pageSpy 收集，有助于调试 
    console.log('查询到用户信息如下\n', data?.user || data);
    if (data?.error || !data?.user) {
        const { t: $tFn } = useNuxtApp().$i18n;
        const tips = (()=>{
            if (data?.error === sTimeout){
                const token = authStore.trueToken || '无token';
                // 用户信息请求的WebSocket事件名
                console.error(`查询用户信息超时 ${iTimeOut}ms`); 
                console.error(`请求参数: { token: "${token.substring(0, 10)}..."}`);
                return $tFn('commonInfo.requestTimeout', { info: 'profile.token' });
            }
            return data?.error || $tFn('commonInfo.requestError');
        })();
        showNegativeNotify(tips);
        throw new Error(`Get user info failed: ${tips}`); 
    }
    data.user.currentCurrency ||= "usdt"; // 提升容错性 
    Object.values(data.user.currencies).forEach((cur: any) => {
        cur.balanceDelay = cur.balance;
    });
    userStore.setUserInfo(data.user);
    const { currentCurrency, lang } = data.user;
    if (currentCurrency) {
        // ↓ 将当前币种也记录在 settingStore 中（userStore中也有保存）提升容错性
        settingStore.pickedCurrency = currentCurrency;
    }
    Sleep(1_200).then(()=>{
        bLangSwitcherVisible.value = true;
    });
    return data.user;
};



/**
 * 移除 URL 中 au 参数（在 au 登录时调用）  
 * 实测得出，此方法在 onMounted() 内部调用更安全，否则会有报错出现  
 * @param lang 语言
 */
async function removeAuCode(lang) {
    console.log('removeAuCode() 开始移除 au 参数，同时跳到用户设定的界面新语言:', lang);
    const newQuery = deepCopy(oRoute.query || {});
    if (newQuery.au) {
        // ↓ 务必要删除，因为这是一次性的值，仅单次有效
        Reflect.deleteProperty(newQuery, 'au');
    } else {
        console.error('Should not be here');
    }
    let sNewPath = oRoute.path;
    if (lang) {
        Cookies.set('locale', lang); // 需要保存，以便刷新后调用此值建立 ws 链接
        sNewPath = useLocalePath()(sNewPath, lang);
    }
    await nextTick(); 
    const oRedirectTo = {
        path: sNewPath,
        query: newQuery,
    };
    console.log('removeAu and navigateTo:\n', deepCopy(oRedirectTo)); 
    await navigateTo(oRedirectTo, {
        replace: true, // 重要设定 
        redirectCode: 302,
    });
    console.log('removeAuCode() 已经移除 au 参数，登录成功');
}

/**
 * 通过 au 获取 token 值（目前仅在服务端调用） 
 */
async function getTokenStringByAU(
    timeout = 1_000 * 5, // x 秒后超时
):Promise<string> {
    console.log('[AU_LOGIN] 开始使用 au 查询 tk 值');
    const { POST_GET_AU_TOKEN } = useAuthApi();
    const oRes = await Promise.race([
        POST_GET_AU_TOKEN(sAuCode),
        new Promise((_, reject) => {
            setTimeout(
                () => reject(new Error('WebSocket token update timeout')),
                timeout, 
            );
        }),
    ]).catch((err)=>{
        console.log('[AU_LOGIN] 通过 au 查询 tk 值出错 ↓ \n', err);
        // ↓ 返回空字符, 表示更新 token 失败了，或超时了
        return '';
    });
    const sToken = oRes?.data?.token || '';
    return sToken;
}

type SearchKey = 'notLoggedIn' | 'logInFailed' | 'connectionFailed';

async function dealWithNoToken(
    sKey:SearchKey = 'notLoggedIn', // 默认提示"未登录" 
) {
    console.log(`dealWithNoToken('${sKey}')`);
    try {
        authStore.setToken('');
        userStore.currencies = {}; // 使表现为未登录
        // ↓ 至此说明用户正在查看游戏结果，让他看！不登录也能看！！！
        if (checkIfViewingHistory()) {
            console.log('历史信息浏览中');
            await Sleep(900);
            bLangSwitcherVisible.value = true;
            return;
        }
        const sNewLang = inferNewLang();
        if (sNewLang) {
            await toRecordNewLanguage(sNewLang);
        }
        await nextTick();
        await Sleep(200); // 没有这行偶有跳转失败 
        // ↓ 从 useRouter() 改用 navigateTo() 因为前者有伴生的错误 
        await navigateTo({
            path: useLocalePath()(`/information`, sNewLang), 
            query: { key: sKey },
        }, {
            replace: true, // 重要设定 
            redirectCode: 302,
        });
    } catch (error) {
        console.log('dealWithNoToken-error', error);
    }
}


// ↓ 在 ws 重连之后执行的事件
// ↓ 处理 ws 二次重联的情况，例如 token 过期，后端发来新值后，用新值重联将触发此方法 
function fnReconnectHandler(iCounter4Connect) {
    console.log('ws 连接计数：', iCounter4Connect);
    // ↓ 建立新监听。重要操作，因为网络波动时也会导致重连
    socket['oTokenPromise_'] = createPromise();
    socket['oTokenPromise_'].oPromise.then(fnReconnectHandler);
    const reconnected = iCounter4Connect > 1;
    if (!reconnected) return;
    // 取出新 token 并保存到 store
    authStore.setToken(socket.auth['token']);
    asyncAuthFetch(); // 更新用户信息
    const sNowGame = getGameName();
    if (!sNowGame) return; // 不在游戏页就跳过
    enterGame(sNowGame, {reconnect: true});
    // ↓ 失去连接时时游戏遮罩会出现，所以二次重连时需要关闭遮罩 
    useLoadingStore().gameLoading = false;
    // 扎金花房间页在重连时不应被父层 key 强制重建，否则会触发“退出牌桌 -> 再进入”
    if (sNowGame === 'zhajinhuaGame') {
        console.log('[ws/reconnect] skip markForReload for zhajinhuaGame');
        return;
    }
    // 其他游戏暂保留旧逻辑（如需可再逐个收敛）
    globalStore.markForReload++;
}

// =======================================================================
// ======= ↓ 最下方是几项生命周期事件，保持在最下方 ====================================== 
// =======================================================================

const ts01 = Date.now();
onBeforeMount(() => {
    console.log('首页 setup -> onBeforeMount() 耗时：', Date.now() - ts01); 
    // 没有 au 登录时可以在此"稍早" 地关闭遮罩，否则在 onMounted 内部伺机关闭 
    if (!sAuCode) {
        const iDelay = 90; // 暂定值，逐步调整延时
        setTimeout(() => {
            // ↓ 去除 loading 遮罩的
            useLoadingStore().showLoading &&= false;
        }, iDelay);
    }
});

onMounted(async () => { 
    console.log('首页 setup -> onMounted() 耗时：', Date.now() - ts01); 
    const oUser = await oUserInfoPromise?.catch(err => {
        console.log('User info err ↓\n', err);
    });
    // console.log('用户信息查询结果：', oUser); 
    // ↓ 顺利登录成功 
    if (oUser?._id > 0){
        // ↓ 在 au 登录时，务必在当前 if() 执行完毕后再设定 isShowRoot 为 true 
        if (sAuCode){
            // ↓ 移除地址栏中的 au 值，重要操作！ 
            await removeAuCode(oUser?.lang);
            console.log('AU 登录成功'); 
        }else{
            // ↓ 已登录用户的日常页面加载
            console.log('常规登录成功'); 
        }
    }else if(oUserInfoPromise || sAuCode){
        // 登录失败了 
        console.log('登录失败'); 
        await dealWithNoToken('logInFailed');
    }else{
        // 压根没登录。通常是因为没有 token 值 
        console.log('未登录'); 
        await dealWithNoToken('notLoggedIn');
    }
    await nextTick(); // 重要，要等以上执行完毕后再显示根节点，因为以上可能有重定向操作 
    isShowRoot.value = true; // 显示根节点 
    nextTick().then(() => {
        useLoadingStore().showLoading &&= false;
    });
    globalStore.setInnerWidth();
    globalStore.setClientMark(); // 需要在根节点显示后再调用 
    // ↓ 在此将 pageLoaded 赋值为 true，这以后的页面跳转交给中间件去拦截 
    // ↓ 在此之前的登录状态验证工作在当前 default.vue 内判断处理
    globalStore.pageLoaded = true;
});
</script>

<style lang="scss" scoped>
.app {
     /* 顶部 header 高度，兼容刘海屏安全区域 */
    --header-height: 46px;
    --header-heigh: calc(var(--header-height) + env(safe-area-inset-top, 0px));
    & > .main-box {
        /* 兼容低版本浏览器的 fallback */
        padding-top: 46px; /* fallback */
        padding-top: var(--header-heigh);
        position: relative;
        // ↓ 进入游戏后的专用背景色，在二期中方案中要保留这个颜色设定 
        &.in-casino{
            background-color: #0d131c; // .bg-\[\#0d131c\]
        }
    }
}
</style>

